/**
 * Auditoria clínico-farmacológica leve.
 * Detecta dados faltantes / inconsistências sem gerar prescrição.
 */
export interface AuditIssue {
  severity: "info" | "warn" | "high";
  code: string;
  message: string;
  suggestion?: string;
}

export interface AuditableSubstance {
  name?: string | null;
  default_curve_model?: string | null;
  default_formulation?: string | null;
  has_steady_state?: boolean | null;
  has_tail?: boolean | null;
  brand_names?: string[] | null;
  pharmacological_class?: string | null;
  clinical_effect_profile?: Record<string, number> | null;
  adverse_effect_profile?: Record<string, number> | null;
  monitoring_profile?: string[] | null;
  cyp_substrate?: string[] | null;
  controlled_substance?: boolean | null;
  dose_low_min?: number | null;
  dose_low_max?: number | null;
  dose_usual_min?: number | null;
  dose_usual_max?: number | null;
  dose_max_recommended?: number | null;
  default_dose_unit?: string | null;
  half_life_min_value?: number | null;
  half_life_max_value?: number | null;
  half_life_unit?: string | null;
  onset_min_value?: number | null;
  peak_min_value?: number | null;
  total_duration_min_value?: number | null;
  interaction_rules?: any;
  source_notes?: string | null;
}

export function auditSubstance(s: AuditableSubstance): AuditIssue[] {
  const out: AuditIssue[] = [];
  if (!s.name?.trim()) out.push({ severity: "high", code: "no_name", message: "Substância sem nome." });
  if (!s.default_curve_model) out.push({ severity: "warn", code: "no_curve_model", message: "Modelo de curva não definido — curva padrão imediata será usada.", suggestion: "Selecionar modelo (immediate_release, prodrug, steady_state_daily, etc.)." });
  if (!s.default_formulation) out.push({ severity: "warn", code: "no_formulation", message: "Sem formulação padrão.", suggestion: "Adicionar pelo menos uma formulação." });
  if (!s.pharmacological_class) out.push({ severity: "info", code: "no_class", message: "Classe farmacológica não preenchida." });
  if (!s.clinical_effect_profile || Object.keys(s.clinical_effect_profile).length === 0)
    out.push({ severity: "info", code: "no_benefit_axes", message: "Eixos terapêuticos 0–100 não definidos." });
  if (!s.adverse_effect_profile || Object.keys(s.adverse_effect_profile).length === 0)
    out.push({ severity: "info", code: "no_adverse_axes", message: "Eixos adversos 0–100 não definidos." });
  if (s.has_steady_state && !s.monitoring_profile?.length)
    out.push({ severity: "warn", code: "ss_no_monitoring", message: "Steady-state ativo sem plano de monitorização." });
  const numeric = [
    ["dose_low_min", s.dose_low_min], ["dose_low_max", s.dose_low_max],
    ["dose_usual_min", s.dose_usual_min], ["dose_usual_max", s.dose_usual_max],
    ["dose_max_recommended", s.dose_max_recommended],
    ["half_life_min_value", s.half_life_min_value], ["half_life_max_value", s.half_life_max_value],
  ] as const;
  for (const [key, value] of numeric) if (value != null && (!Number.isFinite(Number(value)) || Number(value) < 0))
    out.push({ severity: "high", code: `invalid_${key}`, message: `${key} contém valor inválido.` });

  if (s.dose_low_min != null && s.dose_low_max != null && s.dose_low_min > s.dose_low_max)
    out.push({ severity: "high", code: "dose_low_order", message: "Faixa de dose baixa invertida." });
  if (s.dose_usual_min != null && s.dose_usual_max != null && s.dose_usual_min > s.dose_usual_max)
    out.push({ severity: "high", code: "dose_usual_order", message: "Faixa de dose usual invertida." });
  if (s.dose_max_recommended != null && s.dose_usual_max != null && s.dose_max_recommended < s.dose_usual_max)
    out.push({ severity: "warn", code: "dose_max_below_usual", message: "Dose máxima recomendada está abaixo do teto da dose usual." });
  if (s.half_life_min_value != null && s.half_life_max_value != null && s.half_life_min_value > s.half_life_max_value)
    out.push({ severity: "high", code: "half_life_order", message: "Faixa de meia-vida invertida." });
  if (s.peak_min_value != null && s.onset_min_value != null && s.peak_min_value < s.onset_min_value)
    out.push({ severity: "warn", code: "peak_before_onset", message: "Pico cadastrado antes do onset; revisar unidade ou valor." });
  if (s.total_duration_min_value != null && s.onset_min_value != null && s.total_duration_min_value < s.onset_min_value)
    out.push({ severity: "warn", code: "duration_before_onset", message: "Duração total menor que onset; revisar linha do tempo." });

  const rules = s.interaction_rules;
  const rows = Array.isArray(rules?.matrix) ? rules.matrix : [];
  rows.forEach((rule: any, index: number) => {
    if (!String(rule.match_a ?? "").trim() || !String(rule.match_b ?? "").trim())
      out.push({ severity: "high", code: `matrix_pair_${index}`, message: `Regra de interação ${index + 1} sem os dois agentes.` });
    if (!rule.source && !rule.source_url)
      out.push({ severity: "warn", code: `matrix_source_${index}`, message: `Regra de interação ${index + 1} sem fonte rastreável.` });
    if (rule.evidence_grade === "desconhecida")
      out.push({ severity: "info", code: `matrix_evidence_${index}`, message: `Regra de interação ${index + 1} ainda sem grau de evidência.` });
  });

  if (s.controlled_substance && !s.adverse_effect_profile?.["risco de abuso"])
    out.push({ severity: "warn", code: "controlled_no_abuse_axis", message: "Substância controlada sem eixo de risco de abuso preenchido." });
  if (!s.source_notes?.trim())
    out.push({ severity: "info", code: "no_sources", message: "Não há nota/fonte bibliográfica registrada." });
  return out;
}
